import { computed, inject, Injectable, signal } from '@angular/core';
import { TripService } from './trip.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  Activity,
  ActivityType,
  DISTANCE_ONLY_ACTIVITIES,
  DURATION_DAYS_ACTIVITIES,
  DURATION_ONLY_ACTIVITIES,
  MAX_PEOPLE_PER_VEHICLE,
  PEOPLE_PER_VEHICLE_ACTIVITIES,
  PER_RIDE_ACTIVITIES,
} from '../models/activity';
import { CalculationMode } from '../models/calculation-mode.enum';
import { FactorService } from 'src/app/factors/services/factor.service';
import { EmissionFactorCategory } from 'src/app/factors/types/factor';

@Injectable({
  providedIn: 'root',
})
export class ActivityFormService {
  tripService = inject(TripService);
  fb = inject(FormBuilder);
  factorService = inject(FactorService);
  mode = signal<'create' | 'edit'>('create');
  index = signal<number | null>(null);
  activityForm: FormGroup = this.createActivityGroup();
  calculationMode = signal<
    CalculationMode.Distance | CalculationMode.Duration | null
  >(null);

  customFactors = this.factorService.customFactors;
  activityFactor = computed(() => {
    return this.customFactors().filter(
      (factor) => factor.category === EmissionFactorCategory.Activity,
    );
  });

  private getSelectedCustomFactor() {
    const id = this.activityForm.get('customEmissionFactorId')?.value;
    if (!id) return null;
    return this.activityFactor().find((f) => f.id === id) ?? null;
  }

  createActivityGroup(): FormGroup {
    return this.fb.group({
      title: [''],
      type: ['', Validators.required],
      scheduledDays: [[]],
    });
  }
  public resetFormGroup() {
    this.activityForm = this.createActivityGroup();
    this.calculationMode.set(null);
    this.mode.set('create');
    this.index.set(null);
  }

  submitActivity() {
    const activity: Activity = this.activityForm.value;

    let result;
    if (this.mode() === 'edit' && this.index() !== null) {
      result = this.tripService.updateActivity(activity, this.index()!);
    } else {
      result = this.tripService.addActivity(activity);
    }

    // Validation will be triggered by trip service after save completes

    return result;
  }
  editActivity(index: number) {
    this.mode.set('edit');
    this.index.set(index);
    const activity = this.tripService.trip()?.activities[index];
    if (!activity) {
      console.error('could not load activity');
      return;
    }
    this.createConditionalControls(activity);
    // Reset the form with values instead of patchValue to avoid "phantom values"
    this.activityForm.reset(activity);
  }

  get displaySelectCalculationMode() {
    const type = this.activityForm.get('type')?.value;
    if (type === ActivityType.CustomFactor) {
      const factor = this.getSelectedCustomFactor();
      if (!factor) return false;
      const canDist = !!(factor.valuePerKm || (factor.valuePerH && factor.speed));
      const canDur = !!(factor.valuePerH || (factor.valuePerKm && factor.speed));
      return canDist && canDur;
    }
    return type
      ? !DURATION_DAYS_ACTIVITIES.includes(type) &&
          !DURATION_ONLY_ACTIVITIES.includes(type) &&
          !DISTANCE_ONLY_ACTIVITIES.includes(type) &&
          !PER_RIDE_ACTIVITIES.includes(type)
      : false;
  }
  get displayHours() {
    const type = this.activityForm.get('type')?.value;
    if (type === ActivityType.CustomFactor) {
      if (this.calculationMode() === CalculationMode.Duration) return true;
      const factor = this.getSelectedCustomFactor();
      if (!factor) return false;
      const canDist = !!(factor.valuePerKm || (factor.valuePerH && factor.speed));
      const canDur = !!(factor.valuePerH || (factor.valuePerKm && factor.speed));
      return !canDist && canDur;
    }
    return (
      DURATION_ONLY_ACTIVITIES.includes(type) ||
      this.calculationMode() == CalculationMode.Duration
    );
  }

  get displayDays() {
    const type = this.activityForm.get('type')?.value;
    return DURATION_DAYS_ACTIVITIES.includes(type);
  }

  get displayDistance() {
    const type = this.activityForm.get('type')?.value;
    if (type === ActivityType.CustomFactor) {
      if (this.calculationMode() === CalculationMode.Distance) return true;
      const factor = this.getSelectedCustomFactor();
      if (!factor) return false;
      const canDist = !!(factor.valuePerKm || (factor.valuePerH && factor.speed));
      const canDur = !!(factor.valuePerH || (factor.valuePerKm && factor.speed));
      return canDist && !canDur;
    }
    return (
      DISTANCE_ONLY_ACTIVITIES.includes(type) ||
      this.calculationMode() === CalculationMode.Distance
    );
  }

  get displayPassenger() {
    const type = this.activityForm.get('type')?.value;
    return PEOPLE_PER_VEHICLE_ACTIVITIES.includes(type);
  }

  get hasCustomFactor() {
    return this.activityFactor().length > 0;
  }
  get displayCustomEmissionFactors() {
    const type = this.activityForm.get('type')?.value;
    return type === ActivityType.CustomFactor;
  }

  createConditionalControls(activity: Activity) {
    if (activity.customEmissionFactorId) {
      this.createCustomFactorControl();
    }
    if (PEOPLE_PER_VEHICLE_ACTIVITIES.includes(activity.type)) {
      this.createPeoplePerVehicleControl(activity.type);
    }
    if (PER_RIDE_ACTIVITIES.includes(activity.type)) {
      // Forfait : ni durée ni distance
      return;
    }
    if (DURATION_DAYS_ACTIVITIES.includes(activity.type)) {
      this.createDaysControls();
    } else if (DURATION_ONLY_ACTIVITIES.includes(activity.type)) {
      this.createHoursControls();
    } else if (DISTANCE_ONLY_ACTIVITIES.includes(activity.type)) {
      this.createDistanceControls();
    } else if (activity.type !== ActivityType.CustomFactor) {
      if (activity.kilometers && activity.kilometers > 0) {
        this.calculationMode.set(CalculationMode.Distance);
        this.createDistanceControls();
      } else {
        this.calculationMode.set(CalculationMode.Duration);
        this.createHoursControls();
      }
    } else {
      // CustomFactor edit : restaurer le mode depuis les données sauvegardées
      if (activity.kilometers && activity.kilometers > 0) {
        this.calculationMode.set(CalculationMode.Distance);
        this.createDistanceControls();
      } else if (activity.hours && activity.hours > 0) {
        this.calculationMode.set(CalculationMode.Duration);
        this.createHoursControls();
      }
    }
  }

  createDistanceControls() {
    if (!this.activityForm.contains('kilometers'))
      this.activityForm.addControl(
        'kilometers',
        this.fb.control('', [Validators.required, Validators.min(1)]),
      );
  }
  destroyDistanceControls() {
    if (this.activityForm.contains('kilometers')) {
      this.activityForm.removeControl('kilometers');
    }
  }

  createHoursControls() {
    if (!this.activityForm.contains('hours'))
      this.activityForm.addControl(
        'hours',
        this.fb.control('', [Validators.required, Validators.min(0)]),
      );
  }
  destroyHoursControls() {
    if (this.activityForm.contains('hours')) {
      this.activityForm.removeControl('hours');
    }
  }

  createDaysControls() {
    if (!this.activityForm.contains('days'))
      this.activityForm.addControl(
        'days',
        this.fb.control('', [Validators.required, Validators.min(1)]),
      );
  }
  destroyDaysControls() {
    if (this.activityForm.contains('days')) {
      this.activityForm.removeControl('days');
    }
  }
  /**
   * @param activityType surcharge utile en mode édition, où les contrôles sont
   * créés avant que le formulaire ne soit rempli avec l'activité
   */
  createPeoplePerVehicleControl(activityType?: ActivityType) {
    if (!this.activityForm.contains('peoplePerVehicle')) {
      const type = activityType ?? (this.activityForm.get('type')?.value as ActivityType);
      const max = MAX_PEOPLE_PER_VEHICLE[type];
      const validators = [Validators.min(0)];
      if (max) validators.push(Validators.max(max));
      this.activityForm.addControl(
        'peoplePerVehicle',
        this.fb.control(null, validators),
      );
    }
  }
  destroyPeoplePerVehicleControl() {
    if (this.activityForm.contains('peoplePerVehicle')) {
      this.activityForm.removeControl('peoplePerVehicle');
    }
  }

  createCustomFactorControl() {
    if (!this.activityForm.contains('customEmissionFactorId')) {
      this.activityForm.addControl(
        'customEmissionFactorId',
        this.fb.control('', [Validators.required]),
      );
    }
  }
  destroyCustomFactorControl() {
    if (this.activityForm.contains('customEmissionFactorId')) {
      this.activityForm.removeControl('customEmissionFactorId');
    }
  }
}
